import React, { useState, useEffect, useMemo } from 'react';
import { Download } from 'lucide-react';
import { apiService } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Line, LineChart } from 'recharts';

export default function Dashboard() {
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [downloadingGroup, setDownloadingGroup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [weeks, setWeeks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [rows, setRows] = useState([]);

  const downloadCsv = async (groupBy = 'employee') => {
    if (!start || !end) { alert('Sélectionnez une date de début et de fin'); return; }
    try {
      setDownloadingGroup(groupBy);
      const base = apiService.getBaseUrl();
      const headers = apiService.getHeaders();
      const url = `${base}/export/workload.csv?start_date=${encodeURIComponent(start)}&end_date=${encodeURIComponent(end)}&group_by=${encodeURIComponent(groupBy)}`;
      const res = await fetch(url, { headers });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || 'Export échoué');
      }
      const blob = await res.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = `workload_${groupBy}_${start}_${end}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(dlUrl);
    } catch (e) {
      alert(e?.message || 'Erreur export');
    } finally {
      setDownloadingGroup(null);
    }
  };

  // Load JSON data when dates are set
  useEffect(() => {
    const fetchData = async () => {
      if (!start || !end) return;
      try {
        setLoading(true); setError('');
        const base = apiService.getBaseUrl();
        const headers = apiService.getHeaders();
        const url = `${base}/export/workload.json?start_date=${encodeURIComponent(start)}&end_date=${encodeURIComponent(end)}`;
        const res = await fetch(url, { headers });
        if (!res.ok) throw new Error(await res.text());
        const json = await res.json();
        setWeeks(json.weeks || []);
        setProjects(json.projects || []);
        setRows(json.data || []);
      } catch (e) {
        setError(e?.message || 'Erreur chargement');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [start, end]);

  // Prepare chart data: flatten rows into { week, total, [projectLabel]: value }
  const chartData = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    return rows.map((r, idx) => {
      const ws = weeks[idx]?.start || '';
      const obj = { week: r.week, weekStart: ws, total: Number(r.total || 0) };
      (r.values || []).forEach((v, pidx) => {
        const label = projects[pidx]?.label || `Projet ${pidx+1}`;
        obj[label] = Number(v || 0);
      });
      return obj;
    });
  }, [rows, projects, weeks]);

  // Default dates to current year on first render
  useEffect(() => {
    if (!start && !end) {
      const now = new Date();
      const y = now.getFullYear();
      setStart(`${y}-01-01`);
      setEnd(`${y}-12-31`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build ticks and formatters
  const isLongRange = useMemo(() => {
    if (!start || !end) return false;
    const s = new Date(start + 'T00:00:00');
    const e = new Date(end + 'T00:00:00');
    const months = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
    return months > 2; // plus de 2 mois -> afficher les mois
  }, [start, end]);

  const monthTicks = useMemo(() => {
    if (!weeks || weeks.length === 0) return [];
    const ticks = [];
    let prevMonth = null;
    weeks.forEach(w => {
      const d = new Date((w.start || '') + 'T00:00:00');
      const m = d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0');
      if (m !== prevMonth) {
        ticks.push(w.start);
        prevMonth = m;
      }
    });
    return ticks;
  }, [weeks]);

  const allWeekTicks = useMemo(() => (weeks || []).map(w => w.start), [weeks]);

  const tickFormatter = (value) => {
    if (!value) return '';
    const d = new Date(value + 'T00:00:00');
    if (isLongRange) {
      return d.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
    }
    // afficher uniquement la date du lundi (YYYY-MM-DD)
    const y = d.getFullYear();
    const m = String(d.getMonth()+1).padStart(2,'0');
    const day = String(d.getDate()).padStart(2,'0');
    return `${y}-${m}-${day}`;
  };

  const isDownloading = Boolean(downloadingGroup);

  return (
    <div className="py-10 flex items-center justify-center">
      <div className="bg-white shadow rounded-lg p-8 text-center w-full max-w-6xl">
        <h1 className="text-xl font-semibold mb-3">Export charge (CSV)</h1>
        <p className="text-sm text-gray-600 mb-4">Choisissez une plage de dates. Visualisez et exportez la charge hebdomadaire (heures) par projet.</p>
        <div className="flex items-center justify-center gap-3 mb-6">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Début</label>
            <input type="date" value={start} onChange={e => setStart(e.target.value)} className="border rounded px-2 py-1" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Fin</label>
            <input type="date" value={end} onChange={e => setEnd(e.target.value)} className="border rounded px-2 py-1" />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            className={`inline-flex items-center px-4 py-2 rounded shadow ${downloadingGroup === 'employee' ? 'bg-gray-400 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'} ${isDownloading && downloadingGroup !== 'employee' ? 'opacity-60 cursor-not-allowed' : ''}`}
            onClick={() => downloadCsv('employee')}
            disabled={isDownloading}
          >
            <Download className={`w-4 h-4 mr-2 ${downloadingGroup === 'employee' ? 'animate-pulse' : ''}`} />
            {downloadingGroup === 'employee' ? 'Export en cours…' : 'Exporter par Employé'}
          </button>
          <button
            className={`inline-flex items-center px-4 py-2 rounded shadow ${downloadingGroup === 'project' ? 'bg-gray-400 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'} ${isDownloading && downloadingGroup !== 'project' ? 'opacity-60 cursor-not-allowed' : ''}`}
            onClick={() => downloadCsv('project')}
            disabled={isDownloading}
          >
            <Download className={`w-4 h-4 mr-2 ${downloadingGroup === 'project' ? 'animate-pulse' : ''}`} />
            {downloadingGroup === 'project' ? 'Export en cours…' : 'Exporter par Projet'}
          </button>
        </div>

        {/* Chart */}
        <div className="mt-8 text-left">
          {error && <div className="text-red-600 text-sm mb-2">{error}</div>}
          {loading ? (
            <div className="h-64 flex items-center justify-center text-gray-500">Chargement…</div>
          ) : chartData.length === 0 ? (
            <div className="h-32 flex items-center justify-center text-gray-400 text-sm">Aucune donnée pour la plage sélectionnée</div>
          ) : (
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: isLongRange ? 30 : 50 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="weekStart"
                    angle={-30}
                    textAnchor="end"
                    interval={0}
                    height={isLongRange ? 50 : 70}
                    ticks={isLongRange ? monthTicks : allWeekTicks}
                    tickFormatter={tickFormatter}
                  />
                  <YAxis label={{ value: 'Heures', angle: -90, position: 'insideLeft' }} />
                  <Tooltip formatter={(value, name) => [Number(value).toFixed(1) + ' h', name]} />
                  <Legend />
                  {projects.map((p, idx) => (
                    <Bar key={p.label} dataKey={p.label} stackId="stack" fill={`hsl(${(idx*47)%360} 65% 55%)`} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
